import { Component, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { TextFieldTypes } from './input.types';

let inputIds = 0;

export enum DISPLAY_MODE {
	INLINE = 'inline',
	BLOCK = 'block'
}

@Component({
	selector: 'cv-input',
	styleUrls: ['./input.component.scss'],
	templateUrl: './input.component.html'
})
export class InputComponent {

	@Input() className: string;
	/** 
	 * @public identifier for input
	 */
	public inputId = `cv-input-${inputIds++}`;

	/** 
	 * @public If `true`, the user cannot interact with the input.
	 */
	@Input() disabled = false;

	/** 
	 * @public Caption for the input.
	 */
	@Input() label = '';

	/** 
	 * @public The maximum value, which must not be less than its minimum (min attribute) value.
	 */
	@Input() max?: number;

	/**
   * @public If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, 
	 * this attribute specifies the maximum number of characters that the user can enter.
   */
	@Input() maxlength?: number;

	/**
   * @public The minimum value, which must not be greater than its maximum (max attribute) value.
   */
	@Input() min?: number;

	/**
   * @public If the value of the type attribute is `text`, `email`, `search`, `password`, `tel`, or `url`, 
	 * this attribute specifies the minimum number of characters that the user can enter.
   */
	@Input() minlength?: number;

	/**
   * @public The name of the control.
   */
	@Input() name = this.inputId;

	/**
   * @public A regular expression that the value is checked against. The pattern must match the entire value, not just some subset. 
	 * This attribute applies when the value of the type attribute is `text`, `search`, `tel`, `url`, `email`, or `password`, 
	 * otherwise it is ignored.
   */
	@Input() pattern?: string;

	/**
   * @public Instructional text that shows before the input has a value.
   */
	@Input() placeholder = '';

	/**
   * @public If `true`, the user cannot modify the value.
   */
	@Input() readonly = false;

	/**
   * @public The type of control to display. The default type is text.
   */
	@Input() type: TextFieldTypes = 'text';

	/**
	 * @public Whether the input is required field or not.
	 */
	@Input() required: boolean;

	/**
	 * @public Whether the input is required field or not.
	 */
	@Input() displayMode: DISPLAY_MODE | 'inline' | 'block' = DISPLAY_MODE.BLOCK;

	/**
   * @public The value of the control.
   */
	@Input() value?: string | number | null = '';

	/**
   * @public The element of the input control.
   */
	@ViewChild('inputElement', { static: true }) inputElement: ElementRef<any>;

	/** 
	 * @public property that receives if the element value is valid
	 */
	@Input() valid = true;

	/**
	 * @public rows number for the textarea
	 */
	@Input() rows: number;

	/**
   * @public Emitted when a keyboard input ocurred.
   */
	@Output() controlKeyup: EventEmitter<any> = new EventEmitter<any>();
	@Output() clicked: EventEmitter<any> = new EventEmitter<any>();
	@Output() textCleared: EventEmitter<any> = new EventEmitter<any>();

	/**
   * @public Emitted when the value has changed.
   */
	@Output() controlChange: EventEmitter<any> = new EventEmitter<any>();

	/**
	 * @public Emitted when the input loses focus.
   */
	@Output() controlBlur: EventEmitter<any> = new EventEmitter<any>();

	/**
   * @public Emitted when the input has focus.
   */
	@Output() controlFocus: EventEmitter<any> = new EventEmitter<any>();

	constructor() {
		this.rows = this.rows || 3;
	}

	/**
   * change event handler.
   */
	onChange = (event: Event): void => {
		this.controlChange.emit({ 
			instance: this,
			value: (event.target as HTMLInputElement).value,
			isValid: this.isValid()
		});

	}

	/**
	 * On Click event, emit it.
	 */
	onClick(): void {
		this.clicked.emit();
	}

	/**
	 * Determines if text is valid
	 */
	isValid(): boolean {
		let valid = true;
		// TextFieldTypes
		switch (this.type) {
			case 'text': 
			case 'textarea': 
			case 'email': 
			case 'password': 
			case 'url': 
			case 'tel': 
				valid = this.checkRequired();
				break;
		}

		return valid;
	}

	/**
	 * Returns true if text input is valid.
	 */
	checkRequired(): boolean {
		if (this.required) {
			return this.value !== undefined && this.value !== null && this.value.toString().length > 0;
		}
		return true;
	}

	/**
	 * Returns true if type should render a <input> html element.
	 */
	isTextInputType(): boolean {
		return this.type !== 'textarea';
	}
	
	/**
   * keyup event handler.
   */
	onKeyup = (event: Event) => {
		this.value = (event.target as HTMLInputElement).value || '';
		this.controlKeyup.emit({
			instance: this,
			value: this.value,
			isValid: this.isValid(),
			$event: event
		});
	}

	/**
	 * On search event handler.
	 */
	onSearch($event: any): void {
		if ($event && $event.target && !$event.target.value) {
			this.textCleared.emit({
				instance: this,
				value: this.value,
				isValid: this.isValid()
			});
		}
	}

	/**
   * blur event handler.
   */
	onBlur = (event: Event) => {
		this.controlBlur.emit(event);
	}

	/**
   * focus event handler.
   */
	onFocus = (event: Event) => {
		this.controlFocus.emit(event);
	}

	/**
	 * Clear Value of the current Element and emit the change to update the model
	 */
	public clearValue(): void {
		this.value = '';
		this.controlKeyup.emit({
			instance: this,
			value: this.value,
			isValid: this.isValid()
		});
	}

}
